if (!customElements.get('video-reviews-v2')) {
  class VideoReviewsV2 extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      this.addEventListener(
        'click',
        /** @param {MouseEvent} event */
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          const cardElement = target.closest('[data-video-card]');
          if (!(cardElement instanceof HTMLElement)) return;
          if (!cardElement.querySelector('[data-video-template], video')) return;
          this.#toggle(cardElement);
        },
        { signal }
      );

      this.addEventListener(
        'play',
        /** @param {Event} event */
        (event) => {
          this.#pauseOthers(event.target);
          this.#syncCard(event.target);
        },
        { capture: true, signal }
      );

      this.addEventListener(
        'pause',
        /** @param {Event} event */
        (event) => this.#syncCard(event.target),
        { capture: true, signal }
      );

      this.addEventListener(
        'ended',
        /** @param {Event} event */
        (event) => this.#syncCard(event.target),
        { capture: true, signal }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
    }

    /** @param {HTMLElement} cardElement */
    #toggle(cardElement) {
      const videoElement = cardElement.querySelector('video') ?? this.#mountVideo(cardElement);
      if (!(videoElement instanceof HTMLVideoElement)) return;

      if (videoElement.paused) {
        videoElement.play().catch(() => {});
      } else {
        videoElement.pause();
      }
    }

    /** @param {HTMLElement} cardElement */
    #mountVideo(cardElement) {
      const template = cardElement.querySelector('[data-video-template]');
      if (!(template instanceof HTMLTemplateElement)) return null;

      const videoElement = template.content.querySelector('video')?.cloneNode(true);
      if (!(videoElement instanceof HTMLVideoElement)) return null;

      cardElement.querySelector('[data-video-poster]')?.remove();
      cardElement.classList.add('has-video');
      cardElement.prepend(videoElement);
      return videoElement;
    }

    /** @param {EventTarget | null} videoElement */
    #syncCard(videoElement) {
      if (!(videoElement instanceof HTMLVideoElement)) return;
      const cardElement = videoElement.closest('[data-video-card]');
      if (!(cardElement instanceof HTMLElement)) return;

      const isPlaying = !videoElement.paused && !videoElement.ended;
      cardElement.classList.toggle('is-playing', isPlaying);

      const playButton = cardElement.querySelector('[data-video-play]');
      if (playButton instanceof HTMLButtonElement) {
        const label = playButton.getAttribute('aria-label') || '';
        playButton.setAttribute('aria-label', label.replace(/^(Play|Pause)/, isPlaying ? 'Pause' : 'Play'));
      }
    }

    /** @param {EventTarget | null} playingElement */
    #pauseOthers(playingElement) {
      this.querySelectorAll('video').forEach(
        /** @param {HTMLVideoElement} videoElement */
        (videoElement) => {
          if (videoElement !== playingElement && !videoElement.paused) videoElement.pause();
        }
      );
    }
  }

  customElements.define('video-reviews-v2', VideoReviewsV2);
}
